import React from 'react';
import { Box } from '@availity/element';
import type { SxProps } from '@mui/material';

let scriptLoaded = false;

const loadScript = () => {
  if (scriptLoaded) return;
  scriptLoaded = true;
  const script = document.createElement('script');
  script.src = '/static/assets/scripts/figmania-loader.js';
  document.body.appendChild(script);
};

interface AnimationProps {
  src: string;
  sx?: SxProps;
}

const baseSx = {
  width: '100%',
  overflow: 'hidden',
  '& svg': { width: '100%', height: '100%', display: 'block' },
} as const;

const Animation = React.memo(({ src, sx }: AnimationProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const mergedSx = React.useMemo(() => (sx ? { ...baseSx, ...sx } : baseSx), [sx]);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const controller = new AbortController();
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();

        fetch(src, { signal: controller.signal })
          .then((res) => res.text())
          .then((text) => {
            const doc = new DOMParser().parseFromString(text, 'text/html');
            const svgAnimate = doc.querySelector('svg-animate');
            if (svgAnimate) {
              container.appendChild(document.adoptNode(svgAnimate));
              loadScript();
            }
            return null;
          })
          .catch(() => null);
      },
      { threshold: 0.1 }
    );

    observer.observe(container);

    // eslint-disable-next-line consistent-return
    return () => {
      observer.disconnect();
      controller.abort();
    };
  }, [src]);

  return <Box ref={containerRef} sx={mergedSx} />;
});

Animation.displayName = 'Animation';

export default Animation;
